import { EventEmitter, Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { PropertyDocumentSource } from '@barthauer/abc/common-editors';
import { ObjectHelper } from '@barthauer/abc/core';
import { ICoreDataEntity, QueryColumn } from '@barthauer/abc/core/data';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { Subject } from 'rxjs';
import { AdditionalHigonAction, TargetLayoutInfo } from './core/higon/higon.model';
import { AdditionalMenuItem } from './core/menu/menu.model';
import { CRUDPermissions } from './core/models/layout.model';
import { Command, NavigationDocument } from './core/navigation/navigation.model';
import { NavigationNodeBaseViewModel, NavigationViewModel } from './core/navigation/navigation.viewModel';
import { LayoutColumnViewModel, LayoutViewModel } from './core/viewModels/layout.viewModel';
import { IArenaLayout } from './core/arena.component';
import { StylingMode } from '@barthauer/abc/ui-components';
import { AdditionalMenuSettingItem, AdditionalSettingsTab } from './core/settings/setting.model';

export enum LayoutUsage {
  Standard = 'Default',
  New = 'New',
}

export enum ParameterNames {
  IdOfObjectInParentLayout = 'ParentId',
}

export class ArenaUIParams {
  dataform = true;
  drawer = true;
  header = true;
  higna = true;
  hignabuttons = false;
  higon = true;
  menu = true;
  navigation = true;
  standalone = false;

  public getChanges(): {} {
    const result: any = ObjectHelper.cloneDeep(this);
    for (const key in result) {
      if (result[key] === ArenaUIParams.default[key]) {
        result[key] = undefined;
      }
    }
    return result;
  }

  public static default = new ArenaUIParams();
}

export class ArenaUIConfiguration {
  private getParam(func: (p: ArenaUIParams) => boolean): boolean {
    const fromComponent = func(this.component);
    const fromUrl = func(this.url);
    const validValue = !func(ArenaUIParams.default);
    if (fromComponent === validValue || fromUrl === validValue) {
      return validValue;
    } else {
      return !validValue;
    }
  }
  get renderDrawer(): boolean {
    return this.getParam((_) => _.drawer);
  }
  get renderDataForm(): boolean {
    return this.getParam((_) => _.dataform);
  }
  get renderHeader(): boolean {
    return this.getParam((_) => _.header);
  }
  get renderHigna(): boolean {
    return this.level > 1 || this.getParam((_) => _.higna);
  }
  get renderHignaButtons(): boolean {
    return this.renderHigna && this.getParam((_) => _.hignabuttons);
  }
  get renderHigon(): boolean {
    return this.renderDataForm && this.getParam((_) => _.higon);
  }
  get renderMenu(): boolean {
    return this.renderDataForm && this.getParam((_) => _.menu);
  }
  get renderNavigation(): boolean {
    return this.getParam((_) => _.navigation);
  }
  get isStandaloneDataForm(): boolean {
    return this.getParam((_) => _.standalone);
  }

  arenaName: string;
  applicationTitle: string;
  applicationIcon: string;
  stylingMode: StylingMode = StylingMode.Outlined;
  dataFormStyle: DataFormStyle = DataFormStyle.TABS;
  layoutSplitterOrientation: 'horizontal' | 'vertical' = 'vertical';
  propertyDocumentSource: PropertyDocumentSource;
  isReportingDisabled: boolean;
  isQueryingDisabled: boolean;
  helpUrl: string;
  level: number;
  theme: DataFormatTheme;
  public readonly component = new ArenaUIParams();
  public readonly url = new ArenaUIParams();
}

export class NavStateItem {
  layoutPath: string;
  layoutFilter: string;
  parameterValues: ParameterValue[];
  currentId: string;
  currentObjectType: string | number;
  column: string;
  isColumnInTarget: boolean;
  denyInsert: boolean;
  nextFilter: string;
  nextColumn: string;
  isNextColumnInTarget: boolean;
  nextDenyInsert: boolean;
}

export class ArenaDataContext {
  currentid: string;
  objectids: string[];
  objecttype: string;
  navstate: string;
}

export class ArenaLayoutContext {
  readonly data: ArenaDataContext = new ArenaDataContext();
  target: TargetLayoutInfo;
  layout: LayoutViewModel;
  layoutPath: string;
  layoutFilter: string;
  parameterValues: ParameterValue[];
  reload: (currentId: string, requery?: boolean, preserveGridFilter?: boolean) => Promise<void>;
  reloadFiltered: (objectIds: string[]) => Promise<void>;
}

export enum ArenaDefaultGroupMode {
  None = 'None',
  Any = 'Any',
  Layout = 'Layout',
}

export class ArenaContext {
  layoutUsage: LayoutUsage;
  defaultObjectType: string | number;
  openDefaultGroupMode: ArenaDefaultGroupMode;
  nextDataContext: ArenaDataContext = new ArenaDataContext();
}

export class ArenaComponentActions {
  public loadArenaLayout: (
    layoutViewModel: LayoutViewModel,
    layoutPath: string,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
    navstate: NavStateItem[],
  ) => Promise<void>;
  public loadArenaNavigation: (navigationDocument: NavigationViewModel, loadOnly: boolean) => Promise<void>;
  public updateCurrentNavigationNode: (navigationNode: NavigationNodeBaseViewModel) => Promise<void>;
  public refreshArenaObject: (entity: ICoreDataEntity) => Promise<void>;
  public refreshArenaHignaObject: (entity: ICoreDataEntity) => Promise<void>;
  public loadExternalComponent: (command: Command) => Promise<void>;
  public getUIConfiguration: () => ArenaUIConfiguration = () => new ArenaUIConfiguration();
  public getContext: () => ArenaContext = () => new ArenaContext();
  public getDataContext: () => ArenaDataContext = () => new ArenaDataContext();
  public getLayoutContext: () => ArenaLayoutContext = () => new ArenaLayoutContext();
  public getCurrentDataEntity: () => ICoreDataEntity = () => undefined;
  public openSubLayout: (target: TargetLayoutInfo) => Promise<void>;
  public showHideColumn: (column: LayoutColumnViewModel) => Promise<void>;
  public findAdditionalHigonActions: (objectType: string | number) => Promise<AdditionalHigonAction[]>;
  public arenaLayout: IArenaLayout;
}

export interface ISupportsWindowAdapter {
  setWindowAdapter(windowAdapter: IWindowAdapter): void;
}

export interface IWindowAdapter {
  updateWindowQueryParams: (query: URLSearchParams, includeInHistory?: boolean) => void;
  queryParamsStateChanged: EventEmitter<ParamMap>;
}

export class Interfaces {
  public static ISupportsWindowAdapter: ISupportsWindowAdapter = {
    setWindowAdapter: null,
  };
}

@Injectable({
  providedIn: 'root',
})
export class AbcArenaService {
  public actions = new ArenaComponentActions();
  public get context(): ArenaContext {
    return this.actions?.getContext();
  }
  public get lastUrl(): string {
    return sessionStorage.getItem('arena-url');
  }
  public set lastUrl(value: string) {
    if (value) {
      sessionStorage.setItem('arena-url', value);
    }
  }
  public get lastNavState(): string {
    return sessionStorage.getItem('arena-navstate');
  }
  public set lastNavState(value: string) {
    if (value) {
      sessionStorage.setItem('arena-navstate', value);
    }
  }
  public lastHistoryUrl: string;
  public nextUrlFragment: string;
  public isNavigating: boolean;

  private _crudPermissions: CRUDPermissions;
  private arenaLoadStatus: Subject<LoadLayoutStatus> = new Subject();
  private _disableNotification: boolean = false;
  private _disableNotificationOnSuccess: boolean = false;
  private _additionalHigonActions: AdditionalHigonAction[];
  private _additionalSettingsTabs: AdditionalSettingsTab[];
  private _additionalMenuSettingItems: AdditionalMenuSettingItem[] = [];

  public arenaStatus$ = this.arenaLoadStatus.asObservable();
  private _loadCustomNavigationDocument: Promise<NavigationDocument>;
  private _prepareNavigationDocument: (navigationDocument: NavigationDocument) => Promise<void>;

  public get currentDataEntity(): ICoreDataEntity {
    return this.actions.getCurrentDataEntity ? this.actions.getCurrentDataEntity() : undefined;
  }
  public get loadCustomNavigationDocument(): Promise<NavigationDocument> {
    return this._loadCustomNavigationDocument;
  }
  public set loadCustomNavigationDocument(value: Promise<NavigationDocument>) {
    this._loadCustomNavigationDocument = value;
  }
  public get prepareNavigationDocument(): (navigationDocument: NavigationDocument) => Promise<void> {
    return this._prepareNavigationDocument;
  }
  public set prepareNavigationDocument(value: (navigationDocument: NavigationDocument) => Promise<void>) {
    this._prepareNavigationDocument = value;
  }

  public get crudPermissions(): CRUDPermissions {
    return this._crudPermissions;
  }
  public set crudPermissions(crud: CRUDPermissions) {
    this._crudPermissions = crud;
  }
  public arenaStatus(status: LoadLayoutStatus) {
    this.arenaLoadStatus.next(status);
  }

  public get disableNotification(): boolean {
    return this._disableNotification;
  }

  public set disableNotification(disable: boolean) {
    this._disableNotification = disable;
  }
  public get disableNotificationOnSuccess(): boolean {
    return this._disableNotificationOnSuccess;
  }

  public set disableNotificationOnSuccess(disable: boolean) {
    this._disableNotificationOnSuccess = disable;
  }

  private _additionalMenuActions: AdditionalMenuItem[];
  public get additionalMenuActions() {
    return this._additionalMenuActions;
  }
  public set additionalMenuActions(value: AdditionalMenuItem[]) {
    this._additionalMenuActions = value;
  }
  public get additionalHigonActions(): AdditionalHigonAction[] {
    return this._additionalHigonActions;
  }
  public set additionalHigonActions(value: AdditionalHigonAction[]) {
    this._additionalHigonActions = value;
  }

  public get additionalMenuSettingItems(): AdditionalMenuSettingItem[] {
    return this._additionalMenuSettingItems;
  }
  public set additionalMenuSettingItems(value: AdditionalMenuSettingItem[]) {
    this._additionalMenuSettingItems = value;
  }

  public get additionalSettingsTabs(): AdditionalSettingsTab[] {
    return this._additionalSettingsTabs ?? [];
  }
  public set additionalSettingsTabs(value: AdditionalSettingsTab[]) {
    this._additionalSettingsTabs = value;
  }

  constructor() {}
}

export enum Status {
  Success = 'Success',
  Error = 'Error',
}
export interface LoadLayoutStatus {
  Status: Status;
  Message?: string;
  LayoutCaption?: string;
}

export enum DataFormStyle {
  GALLERY = 'GALLERY',
  ACCORDION = 'ACCORDION',
  TABS = 'TABS',
}

export enum DataFormatTheme {
  DEFAULT = 'default',
  COMPACT = 'compact',
}
